import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Link2, Clock, ClipboardList, AlertTriangle, CheckCircle, AlertCircle, Mail, Share2, Calendar, X, CreditCard, Check, Copy, QrCode } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePlan } from '../context/PlanContext';
import VennLinkShareModal from './VennLinkShareModal';
import EmailSignatureGenerator from './EmailSignatureGenerator';
import { SkeletonCard } from './SkeletonLoader';
import { ToastContainer } from './Toast';
import { useToast } from '../hooks/useToast';
import styles from './VennLinkManager.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function VennLinkManager({ userId }) {
  const { isPaid } = usePlan();
  const [vennLinks, setVennLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBookingsModal, setShowBookingsModal] = useState(null); // { linkId, linkTitle }
  const [showShareModal, setShowShareModal] = useState(null); // vennLink object
  const [showEmailSignatureModal, setShowEmailSignatureModal] = useState(null); // vennLink object
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeDefaultCurrency, setStripeDefaultCurrency] = useState('gbp');

  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    if (userId) {
      loadVennLinks();
      checkStripe();
    }
  }, [userId]);

  const checkStripe = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    try {
      const res = await fetch(`${API_URL}/v1/stripe/account`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStripeReady(data.connected && data.stripe_account?.ready);
        if (data.stripe_account?.default_currency) {
          setStripeDefaultCurrency(data.stripe_account.default_currency);
        }
      }
    } catch (err) {
      // Stripe not available — price fields stay hidden
    }
  };

  const loadVennLinks = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/v1/venn-links`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load Venn links');
      }

      const data = await response.json();
      setVennLinks(data.venn_links || []);

    } catch (err) {
      console.error('Error loading venn links:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (newLink) => {
    setVennLinks(prev => [newLink, ...prev]);
    setShowCreateModal(false);
    showSuccess(`Venn link "${newLink.title}" created successfully!`);
  };

  const handleToggleActive = async (linkId, currentActive) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/v1/venn-links/${linkId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: !currentActive })
      });

      if (!response.ok) {
        throw new Error('Failed to update link');
      }

      const data = await response.json();
      setVennLinks(prev => prev.map(link =>
        link.id === linkId ? data.venn_link : link
      ));

    } catch (err) {
      console.error('Error updating link:', err);
      showError('Failed to update link status');
    }
  };

  const handleDelete = async (linkId) => {
    if (!confirm('Are you sure you want to delete this Venn link?')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/v1/venn-links/${linkId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete link');
      }

      setVennLinks(prev => prev.filter(link => link.id !== linkId));
      showSuccess('Venn link deleted successfully');

    } catch (err) {
      console.error('Error deleting link:', err);
      showError('Failed to delete link');
    }
  };

  if (loading) {
    return (
      <div className={styles.skeletonList}>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {successMessage && (
        <div className={styles.alertSuccess}>
          <CheckCircle size={20} className={styles.alertIcon} aria-hidden="true" />
          <p className={styles.alertText}>{successMessage}</p>
        </div>
      )}

      {error && (
        <div className={styles.alertError}>
          <AlertCircle size={20} className={styles.alertIcon} aria-hidden="true" />
          <p className={styles.alertText}>{error}</p>
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Venn Links</h2>
          <p className={styles.headerSubtitle}>Create sharable booking links for your customers</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
          + Create Venn Link
        </button>
      </div>

      {vennLinks.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Link2 size={32} aria-hidden="true" />
          </div>
          <h3 className={styles.emptyTitle}>No Venn Links Yet</h3>
          <p className={styles.emptyBody}>Create your first Venn link to start accepting bookings</p>
          <button onClick={() => setShowCreateModal(true)} className={styles.createBtn}>
            Create Your First Link
          </button>
        </div>
      ) : (
        <div className={styles.list}>
          {vennLinks.map((link) => (
            <div key={link.id} className={styles.linkCard}>
              <div className={styles.linkCardTop}>
                <div className={styles.linkLeft}>
                  <div className={styles.linkMeta}>
                    <h3 className={styles.linkTitle}>{link.title}</h3>
                    <span className={`${styles.statusBadge} ${link.active ? styles.statusBadgeActive : styles.statusBadgeInactive}`}>
                      {link.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {link.description && (
                    <p className={styles.linkDesc}>{link.description}</p>
                  )}
                  <div className={styles.linkStats}>
                    <span className={styles.statItem}>
                      <Clock size={16} aria-hidden="true" />
                      {link.duration_minutes} min
                    </span>
                    <span className={styles.statItem}>
                      <ClipboardList size={16} aria-hidden="true" />
                      {link.bookings_count} booking{link.bookings_count !== 1 ? 's' : ''}
                    </span>
                    {link.max_bookings && (
                      <span className={styles.statItem}>
                        <AlertTriangle size={16} aria-hidden="true" />
                        Max: {link.max_bookings}
                      </span>
                    )}
                    {link.price_amount > 0 && (
                      <span className={styles.statItem}>
                        <CreditCard size={16} aria-hidden="true" />
                        {(link.price_amount / 100).toFixed(2)} {(link.price_currency || 'gbp').toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.urlBar}>
                <div className={styles.urlBarInner}>
                  <code className={styles.urlCode}>{link.url}</code>
                  <div className={styles.urlBtns}>
                    <button
                      onClick={() => setShowEmailSignatureModal(link)}
                      className={styles.emailBadgeBtn}
                      title="Generate Email Signature Badge"
                    >
                      <Mail size={16} aria-hidden="true" />
                      Email Badge
                    </button>
                    <button
                      onClick={() => setShowShareModal(link)}
                      className={styles.shareBtn}
                    >
                      <Share2 size={16} aria-hidden="true" />
                      Share
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.actions}>
                {link.bookings_count > 0 && (
                  <button
                    onClick={() => setShowBookingsModal({ linkId: link.id, linkTitle: link.title, linkUrl: link.url })}
                    className={styles.viewBookingsBtn}
                  >
                    View Bookings ({link.bookings_count})
                  </button>
                )}
                <button
                  onClick={() => handleToggleActive(link.id, link.active)}
                  className={styles.toggleBtn}
                >
                  {link.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => window.open(link.url, '_blank')}
                  className={styles.previewBtn}
                >
                  Preview
                </button>
                <button
                  onClick={() => handleDelete(link.id)}
                  className={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateVennLinkModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          stripeReady={stripeReady}
          stripeDefaultCurrency={stripeDefaultCurrency}
          isPaid={isPaid}
        />
      )}

      {showShareModal && (
        <VennLinkShareModal
          vennLink={showShareModal}
          onClose={() => setShowShareModal(null)}
        />
      )}

      {showEmailSignatureModal && (
        <EmailSignatureGenerator
          vennLink={showEmailSignatureModal}
          businessId={userId}
          onClose={() => setShowEmailSignatureModal(null)}
        />
      )}

      {showBookingsModal && (
        <BookingsModal
          linkId={showBookingsModal.linkId}
          linkTitle={showBookingsModal.linkTitle}
          linkUrl={showBookingsModal.linkUrl}
          onClose={() => setShowBookingsModal(null)}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

// Create Venn Link Modal Component
function CreateVennLinkModal({ onClose, onSuccess, stripeReady, stripeDefaultCurrency, isPaid }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_minutes: 30,
    days_ahead_min: 1,
    days_ahead_max: 14,
    max_bookings: '',
    price_amount: '',
    price_currency: stripeDefaultCurrency
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/v1/venn-links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          max_bookings: formData.max_bookings ? parseInt(formData.max_bookings) : null,
          price_amount: formData.price_amount ? Math.round(parseFloat(formData.price_amount) * 100) : null,
          price_currency: formData.price_amount ? formData.price_currency : null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create Venn link');
      }

      const data = await response.json();
      onSuccess(data.venn_link);

    } catch (err) {
      console.error('Error creating venn link:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderRow}>
            <h2 className={styles.modalTitle}>Create Venn Link</h2>
            <button onClick={onClose} className={styles.modalCloseBtn} aria-label="Close">
              <X size={24} aria-hidden="true" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <div className={styles.formError}>
              <p>{error}</p>
            </div>
          )}

          <div className={styles.formField}>
            <label className={styles.formLabel}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className={styles.formInput}
              placeholder="e.g., 30-Minute Consultation"
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={styles.formTextarea}
              placeholder="What is this meeting about?"
            />
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Duration (minutes) *</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                required
                min="15"
                max="480"
                step="15"
                className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Max Bookings</label>
              <input
                type="number"
                value={formData.max_bookings}
                onChange={(e) => setFormData({ ...formData, max_bookings: e.target.value })}
                min="1"
                className={styles.formInput}
                placeholder="Unlimited"
              />
            </div>
          </div>

          {isPaid && stripeReady && (
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Price</label>
                <input
                  type="number"
                  value={formData.price_amount}
                  onChange={(e) => setFormData({ ...formData, price_amount: e.target.value })}
                  min="0"
                  step="0.01"
                  className={styles.formInput}
                  placeholder="Free"
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Currency</label>
                <select
                  value={formData.price_currency}
                  onChange={(e) => setFormData({ ...formData, price_currency: e.target.value })}
                  className={styles.formInput}
                >
                  <option value="gbp">GBP</option>
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                  <option value="aud">AUD</option>
                  <option value="cad">CAD</option>
                </select>
              </div>
            </div>
          )}
          {!isPaid && (
            <p className={styles.upgradeNote}>
              Want to charge for bookings?{' '}
              <Link to="/settings/billing">Upgrade to Indie</Link>
            </p>
          )}

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Min Days Ahead</label>
              <input
                type="number"
                value={formData.days_ahead_min}
                onChange={(e) => setFormData({ ...formData, days_ahead_min: parseInt(e.target.value) })}
                required
                min="0"
                max="90"
                className={styles.formInput}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Max Days Ahead</label>
              <input
                type="number"
                value={formData.days_ahead_max}
                onChange={(e) => setFormData({ ...formData, days_ahead_max: parseInt(e.target.value) })}
                required
                min="1"
                max="90"
                className={styles.formInput}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="button" onClick={onClose} className={styles.cancelBtn}>
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
              {isSubmitting ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Bookings Modal Component
function BookingsModal({ linkId, linkTitle, linkUrl, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const qrPopoverRef = useRef(null);

  useEffect(() => {
    loadBookings();
  }, [linkId]);

  useEffect(() => {
    if (!showQR) return;
    const handleKey = (e) => { if (e.key === 'Escape') setShowQR(false); };
    const handleClick = (e) => {
      if (qrPopoverRef.current && !qrPopoverRef.current.contains(e.target)) setShowQR(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showQR]);

  const handleCopyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write failed silently
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/v1/venn-links/${linkId}/bookings`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load bookings');
      }

      const data = await response.json();
      setBookings(data.bookings || []);

    } catch (err) {
      console.error('Error loading bookings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTimeRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startTime = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const endTime = endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${startTime} - ${endTime}`;
  };

  const bookingStatusClass = (status) => {
    if (status === 'confirmed') return styles.bookingStatusConfirmed;
    if (status === 'cancelled') return styles.bookingStatusCancelled;
    return styles.bookingStatusPending;
  };

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalLg}`}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderRow}>
            <div>
              <h2 className={styles.modalTitle}>Bookings</h2>
              <p className={styles.modalSubtitle}>for "{linkTitle}"</p>
            </div>
            <button onClick={onClose} className={styles.modalCloseBtn} aria-label="Close">
              <X size={24} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className={styles.bookingsBody}>
          {loading ? (
            <div className={styles.bookingsLoading}>
              <span className={styles.spinner} aria-label="Loading" />
              <p>Loading bookings...</p>
            </div>
          ) : error ? (
            <div className={styles.alertError}>
              <p>{error}</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className={styles.bookingsEmpty}>
              <div className={styles.bookingsEmptyCalendar} aria-hidden="true">
                <Calendar size={40} />
              </div>
              <p className={styles.bookingsEmptyTitle}>No bookings yet</p>
              <p className={styles.bookingsEmptyBody}>Share your booking link to get your first booking.</p>
              <div className={styles.bookingsEmptyCtas}>
                <button
                  className={styles.emptyCopyBtn}
                  onClick={handleCopyLink}
                  disabled={!linkUrl}
                  aria-label="Copy booking link"
                >
                  {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                  {copied ? 'Copied!' : 'Copy booking link'}
                </button>
                <div className={styles.emptyQrWrap} ref={qrPopoverRef}>
                  <button
                    className={styles.emptyQrBtn}
                    onClick={() => setShowQR((v) => !v)}
                    disabled={!linkUrl}
                    aria-label="Show QR code"
                  >
                    <QrCode size={16} aria-hidden="true" />
                    Show QR code
                  </button>
                  {showQR && linkUrl && (
                    <div className={styles.qrPopover} role="dialog" aria-label="QR code">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkUrl)}`}
                        alt="QR code for booking link"
                        width={160}
                        height={160}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.bookingsList}>
              {bookings.map((booking) => (
                <div key={booking.id} className={styles.bookingCard}>
                  <div className={styles.bookingCardTop}>
                    <div className={styles.bookingCustomer}>
                      <h3 className={styles.bookingName}>{booking.customer_name}</h3>
                      <p className={styles.bookingEmail}>{booking.customer_email}</p>
                    </div>
                    <span className={`${styles.bookingStatusBadge} ${bookingStatusClass(booking.status)}`}>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                  </div>

                  <div className={styles.bookingTimeBox}>
                    <div className={styles.bookingTimeRow}>
                      <Calendar size={20} className={styles.bookingTimeIcon} aria-hidden="true" />
                      <span className={styles.bookingDateText}>
                        {formatDateTime(booking.start_time).split(',')[0]},{formatDateTime(booking.start_time).split(',')[1]}
                      </span>
                    </div>
                    <div className={styles.bookingTimeRow}>
                      <Clock size={16} aria-hidden="true" />
                      <span className={styles.bookingTimeText}>{formatTimeRange(booking.start_time, booking.end_time)}</span>
                    </div>
                  </div>

                  {booking.notes && (
                    <div className={styles.bookingNotes}>
                      <p><strong>Notes:</strong> {booking.notes}</p>
                    </div>
                  )}

                  <div className={styles.bookingMeta}>
                    Booked: {new Date(booking.created_at).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
